import { useMemo } from 'react';
import { Link } from 'react-router';
import { marked } from 'marked';
import type { Article } from '../../content/types';
import { SITE_URL, articlePath, collectionBasePath, collectionLabel } from '../../content/types';
import { PageMeta } from '../PageMeta';
import { FAQSection } from '../FAQSection';
import { MarketingHeader } from '../marketing/MarketingHeader';
import { MarketingFooter } from '../MarketingFooter';
import { ContentCTA } from './ContentCTA';

marked.setOptions({ gfm: true, breaks: false });

/** JSON-LD safe for embedding in an inline <script> (prevents </script> breakout). */
function jsonLd(data: Record<string, unknown>): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

interface ArticleLayoutProps {
  article: Article;
}

/**
 * Shared layout for guides, use-cases, and alternatives listicles.
 *
 * Renders the markdown body, breadcrumb, FAQ, related links, and CTA — and
 * emits Breadcrumb + Article/HowTo + (optional) ItemList + FAQPage JSON-LD as
 * inline <script> tags so the structured data is present in the pre-rendered
 * HTML (better for AI/answer-engine crawlers than client-only injection).
 */
export function ArticleLayout({ article }: ArticleLayoutProps) {
  const url = `${SITE_URL}${articlePath(article)}`;
  const ogImage = `${SITE_URL}${article.ogImage || '/og-image.png'}`;
  const bodyHtml = useMemo(() => marked.parse(article.body) as string, [article.body]);

  const breadcrumbLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        {
          '@type': 'ListItem',
          position: 2,
          name: collectionLabel(article.collection),
          item: `${SITE_URL}${collectionBasePath(article.collection)}`,
        },
        { '@type': 'ListItem', position: 3, name: article.title, item: url },
      ],
    }),
    [article.collection, article.title, url],
  );

  const mainLd = useMemo(() => {
    const author = article.author
      ? { '@type': 'Person', name: article.author.name, ...(article.author.url ? { url: article.author.url } : {}) }
      : { '@type': 'Organization', name: 'Pollenate', url: SITE_URL };
    const base: Record<string, unknown> = {
      '@context': 'https://schema.org',
      headline: article.title,
      description: article.description,
      datePublished: article.datePublished,
      dateModified: article.dateModified,
      author,
      publisher: {
        '@type': 'Organization',
        name: 'Pollenate',
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon-light.svg` },
      },
      mainEntityOfPage: url,
      image: ogImage,
    };
    if (article.schemaType === 'HowTo' && article.howToSteps?.length) {
      return {
        ...base,
        '@type': 'HowTo',
        name: article.title,
        step: article.howToSteps.map((s, i) => ({
          '@type': 'HowToStep',
          position: i + 1,
          name: s.name,
          text: s.text,
        })),
      };
    }
    return { ...base, '@type': 'Article' };
  }, [article, url, ogImage]);

  const itemListLd = useMemo(() => {
    if (!article.itemList?.length) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: article.itemList.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: it.name,
        ...(it.url ? { url: it.url } : {}),
      })),
    };
  }, [article.itemList]);

  const faqLd = useMemo(() => {
    if (!article.faqs?.length) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    };
  }, [article.faqs]);

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-neutral-950">
      <PageMeta
        title={article.metaTitle}
        description={article.description}
        url={url}
        type="article"
        image={ogImage}
      />

      {/* Inline structured data (present in pre-rendered HTML for crawlers) */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(mainLd) }} />
      {itemListLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(itemListLd) }} />
      )}
      {faqLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(faqLd) }} />
      )}

      <MarketingHeader />

      <main id="main-content" className="px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <article className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 text-sm">
            <ol className="flex flex-wrap items-center gap-1.5 text-slate-500 dark:text-neutral-400">
              <li>
                <Link to="/" className="hover:text-slate-900 dark:hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link
                  to={collectionBasePath(article.collection)}
                  className="hover:text-slate-900 dark:hover:text-white transition-colors"
                >
                  {collectionLabel(article.collection)}
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-slate-700 dark:text-neutral-300" aria-current="page">
                {article.title}
              </li>
            </ol>
          </nav>

          {/* Hero */}
          <header>
            <p className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
              {article.eyebrow}
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
              {article.title}
            </h1>
            <p className="mt-4 text-lg text-slate-600 dark:text-neutral-400">{article.heroSubtitle}</p>
            {(article.author || article.readingTime) && (
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500 dark:text-neutral-500">
                {article.author && <span>{article.author.name}</span>}
                {article.author && article.readingTime && <span aria-hidden="true">·</span>}
                {article.readingTime && <span>{article.readingTime}</span>}
              </div>
            )}
          </header>

          {/* Body */}
          <div
            className="prose prose-slate dark:prose-invert max-w-none mt-8 prose-headings:scroll-mt-24 prose-a:text-amber-600 dark:prose-a:text-amber-400 prose-a:font-medium prose-table:text-sm"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />

          {/* Related */}
          {article.related?.length ? (
            <aside className="mt-12 border-t border-slate-200 dark:border-neutral-800 pt-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-neutral-400">
                Keep reading
              </h2>
              <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                {article.related.map((r) => (
                  <li key={r.to}>
                    <Link to={r.to} className="text-amber-600 dark:text-amber-400 hover:underline">
                      {r.label} &rarr;
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          ) : null}
        </article>
      </main>

      {article.faqs?.length ? (
        <div className="border-t border-slate-200 dark:border-neutral-800">
          <FAQSection faqs={article.faqs} id={`${article.collection}-${article.slug}`} injectSchema={false} />
        </div>
      ) : null}

      <ContentCTA />

      <MarketingFooter />
    </div>
  );
}
